import {
  CellMeasurer,
  CellMeasurerCache,
  List,
  AutoSizer,
  InfiniteLoader,
} from 'react-virtualized';
import { useChatContext } from '@/contexts/chat/context';
import type { User } from '@/configs/apis/user';
import { type Chat } from '@/contexts/chat/scripts/hooks/useChats/types';
import { groupsApis } from '@/configs/apis/groups';
import type { ChatMediaInfoPartial } from '@/contexts/chat/scripts/hooks/useChatMediaCall/types';
import { ChatMessagePayloadType } from '@/contexts/chat/scripts/hooks/useChatMessages/types';
import MessageItem from './components/messageItem';
import styles from './index.module.scss';
import SelectContact from './components/select-contact';

// const PAGE_SIZE = 20;
const PAGE_SIZE = 100000; // all data is -1

const MessageList = ({ user }: { user: User }) => {
  const { chatsState, activeChat, loadChats, syncMediaCallingInfo } = useChatContext();
  const activatedChatId = chatsState.activatedChatId;
  const dataList = chatsState.chats;
  const pageCurrent = useRef(0);

  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const cacheRef = useRef(
    new CellMeasurerCache({
      defaultHeight: 48,
      fixedWidth: true,
      minHeight: 48,
    })
  );

  const handleClick = useCallback(
    (item: Chat) => {
      activeChat(item.chat_id);
    },
    [activeChat]
  );

  const queryGroupMediaCalling = useCallback(
    async (chats: Chat[]) => {
      const res = await groupsApis.groupMediaCalling(chats.map((chat) => chat.chat_id));
      const chatMediaInfoList = chats.reduce((acc, chat) => {
        const info = res?.list.find((item) => item.gid === chat.chat_id);
        if (info) {
          acc.push({
            chat,
            room_id: info.room_id,
            payload_type:
              info.type === 0
                ? ChatMessagePayloadType.MEDIA_AUDIO_CALL
                : ChatMessagePayloadType.MEDIA_VIDEO_CALL,
          });
        }
        return acc;
      }, [] as ChatMediaInfoPartial[]);
      chatMediaInfoList.map(async (chatMediaInfo) => {
        await syncMediaCallingInfo(chatMediaInfo);
      });
    },
    [syncMediaCallingInfo]
  );

  const queryList = useCallback(
    async (limit: number, offset: number) => {
      if (!hasMore || loading) return Promise.resolve();

      try {
        setLoading(true);
        const chats = await loadChats(user, limit, offset);
        pageCurrent.current += 1;
        setHasMore(chats?.length >= PAGE_SIZE);
        queryGroupMediaCalling(chats);
      } catch (error) {
        console.error('Failed to load chats:', error);
      } finally {
        setLoading(false);
      }
    },
    [hasMore, loadChats, loading, queryGroupMediaCalling, user]
  );

  const rowCount = hasMore ? dataList.length + PAGE_SIZE : dataList.length;

  const isRowLoaded = ({ index }: { index: number }) => index < dataList.length;

  const loadMoreRows = () => queryList(PAGE_SIZE, pageCurrent.current * PAGE_SIZE);

  const rowRenderer = useCallback(
    ({ index, parent, style, columnIndex }: any) => {
      const item = dataList[index];
      if (!item) return null;
      return (
        <CellMeasurer
          key={`cell-${item.chat_id}`}
          cache={cacheRef.current}
          columnIndex={columnIndex}
          parent={parent}
          rowIndex={index}
        >
          {({ registerChild }) => (
            <div ref={registerChild} data-index={index} style={style}>
              <MessageItem
                key={`message-${item.chat_id}`}
                chat={item}
                activatedChatId={activatedChatId}
                onClick={() => handleClick(item)}
              />
            </div>
          )}
        </CellMeasurer>
      );
    },
    [activatedChatId, dataList, handleClick]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chat_id = urlParams.get('chat_id');
    if (chat_id) {
      const timerId = setTimeout(() => {
        clearTimeout(timerId);
        activeChat(chat_id);
      }, 2000);
    }
  }, [activeChat]);

  return (
    <div className={styles.messageList}>
      <AutoSizer>
        {({ width, height }) => (
          <InfiniteLoader
            isRowLoaded={isRowLoaded}
            loadMoreRows={loadMoreRows}
            rowCount={rowCount}
            threshold={20}
          >
            {({ onRowsRendered, registerChild }) => (
              <List
                className="scrollbar_hover"
                ref={registerChild}
                width={width}
                height={height}
                deferredMeasurementCache={cacheRef.current}
                rowHeight={cacheRef.current.rowHeight}
                rowCount={rowCount}
                rowRenderer={rowRenderer}
                onRowsRendered={onRowsRendered}
                noRowsRenderer={() => <SelectContact />}
                scrollToIndex={0}
              />
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
    </div>
  );
};

export default MessageList;
